import type { ComputedDeliverable } from "hoxton-types";
import type { Annotation, QAAnnotationComment, ConstructedDeliverable } from "@/types/types";

import { apolloClient } from "@/apollo";
import AddQAAnnotationCommentOnComputedDeliverableMutation from "@/apollo/mutations/v2/annotations/AddQAAnnotationCommentOnComputedDeliverable.gql";
import AddQAAnnotationOnComputedDeliverableMutation from "@/apollo/mutations/v2/annotations/AddQAAnnotationOnComputedDeliverable.gql";
import ChangeQAAnnotationStatusOnComputedDeliverableMutation from "@/apollo/mutations/v2/annotations/ChangeQAAnnotationStatusOnComputedDeliverable.gql";
import RemoveQAAnnotationCommentOnComputedDeliverableMutation from "@/apollo/mutations/v2/annotations/RemoveQAAnnotationCommentOnComputedDeliverable.gql";
import RemoveQAAnnotationOnComputedDeliverableMutation from "@/apollo/mutations/v2/annotations/RemoveQAAnnotationFromComputedDeliverable.gql";
import UpdateQAAnnotationOnComputedDeliverableMutation from "@/apollo/mutations/v2/annotations/UpdateQAAnnotationOnComputedDeliverable.gql";
import UpdateQAAnnotationCommentOnComputedDeliverableMutation from "@/apollo/mutations/v2/annotations/UpdateQAAnnotationCommentOnComputedDeliverable.gql";
import SetComputedDeliverableStatusMutation from "@/apollo/mutations/v2/SetComputedDeliverableStatus.gql";
import SetComputedDeliverableVisibleToMutation from "@/apollo/mutations/v2/SetComputedDeliverableVisibleTo.gql";
import ComputedDeliverablesStatusQuery from "@/apollo/queries/v2/ComputedDeliverablesStatus.gql";
import ComputedDeliverablesAnnotationsQuery from "@/apollo/queries/v2/ComputedDeliverablesAnnotations.gql";
import getComputedDeliverableIdentifiers from "@/services/deliverables/getComputedDeliverableIdentifiers";
import { isEmptyObject } from "../utils";
import type { ConstructedLibraryFilters } from "@/utils/constructLibraryFiltersQueryVariables";

export async function addAnnotationToDeliverable(
    campaignId: string,
    deliverable: ConstructedDeliverable,
    content: string,
    time: number,
    posX: number,
    posY: number,
    mentions: string[] = []
) {
    const [computedDeliverableIdentifier] = getComputedDeliverableIdentifiers([deliverable]);

    const { data } = await apolloClient.mutate<{
        addQAAnnotationOnComputedDeliverable: Annotation;
    }>({
        mutation: AddQAAnnotationOnComputedDeliverableMutation,
        variables: {
            campaignId,
            computedDeliverableIdentifier,
            description: content,
            timeline: time,
            xpos: posX,
            ypos: posY,
            mentions
        }
    });

    if (!data) {
        throw new Error("Failed to add annotation to deliverable");
    }

    return data.addQAAnnotationOnComputedDeliverable;
}

export async function addCommentToAnnotation(annotationId: string, content: string, mentions: string[] = []) {
    const { data } = await apolloClient.mutate<{
        addQAAnnotationCommentOnComputedDeliverable: QAAnnotationComment;
    }>({
        mutation: AddQAAnnotationCommentOnComputedDeliverableMutation,
        variables: {
            annotationId,
            content,
            mentions
        }
    });
    if (!data) {
        throw new Error("Failed to add comment");
    }

    return data.addQAAnnotationCommentOnComputedDeliverable;
}

export async function deleteAnnotation(annotationId: string) {
    await apolloClient.mutate({
        mutation: RemoveQAAnnotationOnComputedDeliverableMutation,
        variables: {
            annotationId
        }
    });
}

export async function deleteAnnotationComment(commentId: string) {
    await apolloClient.mutate({
        mutation: RemoveQAAnnotationCommentOnComputedDeliverableMutation,
        variables: {
            commentId
        }
    });
}

export async function getAnnotationsOfDeliverable(
    campaignId: string,
    deliverable: ConstructedDeliverable,
    annotationType: string
) {
    const computedDeliverableIdentifiers = getComputedDeliverableIdentifiers([deliverable]);
    const { data } = await apolloClient.query<{
        computedDeliverables: { annotations: Annotation[] }[];
    }>({
        fetchPolicy: "network-only",
        query: ComputedDeliverablesAnnotationsQuery,
        variables: {
            annotationType,
            campaignId,
            computedDeliverableIdentifiers
        }
    });

    if (!data?.computedDeliverables?.length) {
        return [];
    }

    return data.computedDeliverables[0].annotations;
}

export async function getDeliverables(campaignId: string, deliverables: ConstructedDeliverable[]) {
    const computedDeliverableIdentifiers = getComputedDeliverableIdentifiers(deliverables);
    const { data } = await apolloClient.query<{
        computedDeliverables: ComputedDeliverable[];
    }>({
        fetchPolicy: "network-only",
        query: ComputedDeliverablesStatusQuery,
        variables: {
            campaignId,
            computedDeliverableIdentifiers
        }
    });

    return data.computedDeliverables;
}

export async function setAnnotationStatus(annotationId: string, status: string) {
    const { data } = await apolloClient.mutate<{
        changeQAAnnotationStatusOnComputedDeliverable: Annotation;
    }>({
        mutation: ChangeQAAnnotationStatusOnComputedDeliverableMutation,
        variables: {
            annotationId,
            annotationStatus: status
        }
    });

    if (!data) {
        throw new Error(`Failed to update annotation status for ID: ${annotationId}`);
    }

    return data.changeQAAnnotationStatusOnComputedDeliverable;
}

export async function setDeliverablesGlobalStatus(
    campaignId: string,
    deliverables: ConstructedDeliverable[],
    status: string,
    libraryFilters: ConstructedLibraryFilters | null
) {
    const variables: Record<string, unknown> = {
        campaignId,
        globalStatus: status
    };

    if (libraryFilters && !isEmptyObject(libraryFilters)) {
        // all selected mode
        variables.libraryFilters = libraryFilters;
    } else {
        const computedDeliverableIdentifiers = getComputedDeliverableIdentifiers(deliverables);
        variables.computedDeliverableIdentifiers = computedDeliverableIdentifiers;
    }

    const { data } = await apolloClient.mutate<{
        setComputedDeliverablesQAStatus: { count: number; success: boolean };
    }>({
        mutation: SetComputedDeliverableStatusMutation,
        variables
    });

    return data!.setComputedDeliverablesQAStatus;
}

export async function setDeliverablesVisibleToRoles(
    campaignId: string,
    deliverables: ConstructedDeliverable[],
    visibleToRoleIds: string[],
    libraryFilters: ConstructedLibraryFilters | null
) {
    const variables: Record<string, unknown> = {
        campaignId,
        visibleTo: visibleToRoleIds
    };

    if (libraryFilters && !isEmptyObject(libraryFilters)) {
        // all selected mode
        variables.libraryFilters = libraryFilters;
    } else {
        const computedDeliverableIdentifiers = getComputedDeliverableIdentifiers(deliverables);
        variables.computedDeliverableIdentifiers = computedDeliverableIdentifiers;
    }

    const { data } = await apolloClient.mutate<{
        setComputedDeliverablesVisibility: { count: number; success: boolean };
    }>({
        mutation: SetComputedDeliverableVisibleToMutation,
        variables
    });

    return data!.setComputedDeliverablesVisibility;
}

export async function setLoggedInUsersDeliverablesStatus(
    campaignId: string,
    deliverables: ConstructedDeliverable[],
    status: string,
    libraryFilters: ConstructedLibraryFilters | null
) {
    const variables: Record<string, unknown> = {
        campaignId,
        userStatus: status
    };

    if (libraryFilters && !isEmptyObject(libraryFilters)) {
        // all selected mode
        variables.libraryFilters = libraryFilters;
    } else {
        const computedDeliverableIdentifiers = getComputedDeliverableIdentifiers(deliverables);
        variables.computedDeliverableIdentifiers = computedDeliverableIdentifiers;
    }

    const { data } = await apolloClient.mutate<{
        setComputedDeliverablesQAStatus: { count: number; success: boolean };
    }>({
        mutation: SetComputedDeliverableStatusMutation,
        variables
    });

    return data!.setComputedDeliverablesQAStatus;
}

export async function updateAnnotation(
    annotationId: string,
    content: string,
    time: number,
    posX: number,
    posY: number,
    mentions: string[] = []
) {
    const { data } = await apolloClient.mutate<{
        updateQAAnnotationOnComputedDeliverable: Annotation;
    }>({
        mutation: UpdateQAAnnotationOnComputedDeliverableMutation,
        variables: {
            annotationId,
            description: content,
            timeline: time,
            xpos: posX,
            ypos: posY,
            mentions
        }
    });

    if (!data) {
        throw new Error(`Failed to update annotation with ID: ${annotationId}`);
    }

    return data.updateQAAnnotationOnComputedDeliverable;
}

export async function updateAnnotationComment(commentId: string, content: string, mentions: string[] = []) {
    const { data } = await apolloClient.mutate<{
        updateQAAnnotationCommentOnComputedDeliverable: QAAnnotationComment;
    }>({
        mutation: UpdateQAAnnotationCommentOnComputedDeliverableMutation,
        variables: {
            commentId,
            content,
            mentions
        }
    });

    if (!data) {
        throw new Error(`Failed to update annotation comment with ID: ${commentId}`);
    }

    return data.updateQAAnnotationCommentOnComputedDeliverable;
}
